import type { AccessLevel, Entitlement, UserProfile } from "@woven/contracts";
import type { User } from "firebase/auth";
import { createContext, useContext } from "react";

export type AccessSource = "firestore" | "mock" | "local" | "default";

export type ReauthenticationRequest =
  { providerId: "password"; password: string } | { providerId: "google.com" | "github.com" };

export interface AuthSession {
  uid: string;
  generation: number;
}

export interface AuthContextValue {
  user: User | null;
  session: AuthSession | null;
  isCurrentSession: (session: AuthSession) => boolean;
  refreshMembership: (options?: { background?: boolean; signal?: AbortSignal }) => Promise<void>;
  accessRequested: boolean | null;
  /** True until session restoration and authoritative membership loading complete. */
  loading: boolean;
  profile: UserProfile | null;
  entitlement: Entitlement | null;
  access: AccessLevel;
  /**
   * Where the current access level came from:
   * - `firestore` — real `entitlements/{uid}` document
   * - `mock` — explicit dev-only mock data (never a signup fallback)
   * - `local` — dev-only local admin bypass (never authenticated or persisted)
   * - `default` — fail-closed guest access while membership is unavailable
   */
  accessSource: AccessSource;
  usingMockAccess: boolean;
  error: Error | null;
  signInWithGoogle: () => Promise<AuthSession>;
  signInWithGithub: () => Promise<AuthSession>;
  /** Signs in an existing email/password account. */
  signInWithEmail: (email: string, password: string) => Promise<AuthSession>;
  /** Creates a new email/password account and signs it in. */
  createAccountWithEmail: (email: string, password: string) => Promise<AuthSession>;
  reauthenticate: (request: ReauthenticationRequest) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function synthesizeProfile(authUser: User, timestamp: string): UserProfile {
  return {
    uid: authUser.uid,
    email: authUser.email ?? null,
    displayName: authUser.displayName ?? null,
    photoUrl: authUser.photoURL ?? null,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}
